import React, { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasAccess, formatDate } from "@/lib/utils2";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, ChevronUp, ChevronDown, Trash2, MessageSquareHeart } from "lucide-react";

export default function Feedback() {
  const { user } = useAuth();
  const isAdmin = hasAccess(user, "admin");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/feedback");
    setItems(data.feedback);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Please add a title.");
    setBusy(true);
    try {
      await api.post("/feedback", form);
      toast.success("Topic submitted — thanks!");
      setForm({ title: "", body: "" });
      setOpen(false);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  const vote = async (id, direction) => {
    try {
      const { data } = await api.post(`/feedback/${id}/vote?direction=${direction}`);
      setItems((prev) => prev.map((f) => (f.id === id ? data.feedback : f)).sort((a, b) => b.score - a.score));
    } catch (err) { toast.error(errMsg(err)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/feedback/${id}`); toast.success("Removed"); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-4xl">
      <PageHeader title="Feedback & Topics" subtitle="Suggest episode topics and vote on what Elias and Maya cover next.">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full brand-btn" data-testid="new-feedback-button"><Plus className="w-4 h-4 mr-2" /> Suggest a topic</Button>
          </DialogTrigger>
          <DialogContent className="border-brand-line">
            <DialogHeader><DialogTitle className="font-display text-2xl">Suggest a topic</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Title</Label><Input data-testid="feedback-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5 border-brand-line" /></div>
              <div><Label>Details</Label><Textarea data-testid="feedback-body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={4} className="mt-1.5 border-brand-line" /></div>
            </div>
            <DialogFooter><Button onClick={submit} disabled={busy} className="rounded-full brand-btn" data-testid="submit-feedback-button">Submit</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="space-y-3" data-testid="feedback-list">
        {items.length === 0 && (
          <div className="rounded-2xl border border-dashed border-brand-line p-10 text-center">
            <MessageSquareHeart className="w-8 h-8 text-brand-muted mx-auto" />
            <p className="text-brand-muted mt-3">No topics yet. Be the first to suggest one!</p>
          </div>
        )}
        {items.map((f) => (
          <div key={f.id} className="rounded-2xl border border-brand-line bg-white p-5 flex gap-4" data-testid={`feedback-item-${f.id}`}>
            <div className="flex flex-col items-center gap-1 shrink-0">
              <button onClick={() => vote(f.id, "up")} data-testid={`upvote-${f.id}`}
                className={`h-8 w-8 rounded-lg flex items-center justify-center border transition-colors duration-200 ${f.my_vote === 1 ? "bg-brand text-white border-brand" : "border-brand-line hover:bg-brand-sidebar"}`}>
                <ChevronUp className="w-5 h-5" />
              </button>
              <span className="font-display text-lg text-brand-ink tabular-nums" data-testid={`score-${f.id}`}>{f.score}</span>
              <button onClick={() => vote(f.id, "down")} data-testid={`downvote-${f.id}`}
                className={`h-8 w-8 rounded-lg flex items-center justify-center border transition-colors duration-200 ${f.my_vote === -1 ? "bg-brand-terracotta text-white border-brand-terracotta" : "border-brand-line hover:bg-brand-sidebar"}`}>
                <ChevronDown className="w-5 h-5" />
              </button>
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-brand-ink">{f.title}</p>
              {f.body && <p className="text-brand-muted mt-1 leading-relaxed">{f.body}</p>}
              <p className="text-xs text-brand-muted mt-2">{f.author_name} · {formatDate(f.created_at)} · ▲{f.upvote_count} ▼{f.downvote_count}</p>
            </div>
            {isAdmin && (
              <Button size="icon" variant="ghost" onClick={() => remove(f.id)} className="text-brand-terracotta shrink-0" data-testid={`delete-feedback-${f.id}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
